import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  Flex,
  Heading,
  IconButton,
  Switch,
  Text,
  toast,
  Input,
  Button,
  FormLabel
} from '@sparrowengg/twigs-react';
import {
  CloseIcon,
  DeleteIcon,
  SettingsIcon
} from '@sparrowengg/twigs-react-icons';
import { truncateText } from 'commons/helper';
import { devPortalServices } from 'commons/services';
import { IRootDispatch } from 'commons/types';
import { WebhookDetailsType } from 'features/dashboard/types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setWebhookApp } from 'redux-store/actions';
import { warningModalStates } from '../constants';

const defaultShowModalState = {
  show: false,
  type: ''
};

const SingleWebhook = ({
  webhook,
  getAllWebhookApps
}: {
  webhook: WebhookDetailsType;
  getAllWebhookApps: () => Promise<void>;
}) => {
  const dispatch: IRootDispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(defaultShowModalState);
  const [btnLoader, setBtnLoader] = useState(false);

  const disableWebhook = async () => {
    setBtnLoader(true);
    try {
      await devPortalServices.disableWebhook(webhook.webhookId);
      toast({
        variant: 'success',
        description: 'Webhook has been disabled Successfully'
      });
      getAllWebhookApps();
      setShowModal(defaultShowModalState);
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Error in disabling Webhook'
      });
    } finally {
      setBtnLoader(false);
    }
  };

  const deleteWebhook = async () => {
    setBtnLoader(true);
    try {
      await devPortalServices.deleteWebhook(webhook.webhookId);
      toast({
        variant: 'success',
        description: 'Webhook has been disabled Successfully'
      });
      getAllWebhookApps();
      setShowModal(defaultShowModalState);
    } catch (error) {
      console.error(error);
      toast({
        variant: 'error',
        description: 'Error in deleting Webhook'
      });
    } finally {
      setBtnLoader(false);
    }
  };

  return (
    <Flex
      alignItems="center"
      gap="$8"
      css={{
        '&:hover': {
          '.delete-btn': {
            opacity: 1
          }
        }
      }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        css={{
          flex: 1,
          padding: '$6',
          borderRadius: '$xl',
          border: '$borderWidths$xs solid $neutral200'
        }}
      >
        <Flex alignItems="center">
          <Avatar src={webhook.icon} size="lg" rounded="sm" />
          <Box css={{ marginLeft: '$5' }}>
            <Text size="md" weight="medium" css={{ whiteSpace: "break-spaces" }}>
              {truncateText(webhook.name, 80)}
            </Text>
            <Text size="sm" css={{ color: '$neutral800' }}>
              {truncateText(webhook.description, 70)}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="center" gap="$6" css={{ marginRight: '$8' }}>
          <IconButton
            icon={<SettingsIcon size={24} />}
            size="lg"
            variant="ghost"
            color="default"
            onClick={() => {
              dispatch(setWebhookApp(webhook));
              navigate(`/add-webhook?id=${webhook.webhookId}`);
            }}
          />
          <Switch
            checked={webhook.validated}
            css={{ cursor: 'pointer' }}
            size="md"
            onChange={(value) => {
              if (!value) {
                setShowModal({
                  type: warningModalStates.DISABLE,
                  show: true
                });
              } else {
                dispatch(setWebhookApp(webhook));
                navigate(
                  `/add-webhook?page=authenticate&id=${webhook?.webhookId}`
                );
              }
            }}
          />
        </Flex>
      </Flex>
      <IconButton
        icon={<DeleteIcon />}
        size="lg"
        color="default"
        variant="ghost"
        className="delete-btn"
        onClick={() => {
          setShowModal({
            type: 'delete',
            show: true
          });
        }}
        css={{ opacity: 0 }}
      />
      {showModal.show && (
        <WarningModal
          resetModal={() => setShowModal(defaultShowModalState)}
          onClickHandler={() => {
            if (showModal.type === warningModalStates.DELETE) {
              deleteWebhook();
            } else {
              disableWebhook();
            }
          }}
          btnLoader={btnLoader}
          webhookName={webhook.name}
          isDeleteWebhook={showModal.type === warningModalStates.DELETE}
        />
      )}
    </Flex>
  );
};

const WarningModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  webhookName,
  isDeleteWebhook
}: {
  resetModal: () => void;
  btnLoader: boolean;
  onClickHandler: () => void;
  webhookName: string;
  isDeleteWebhook: boolean;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 476 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">{`Webhook ${
            isDeleteWebhook ? 'Deletion' : 'Disable'
          } Confirmation`}</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              {`To ${
                isDeleteWebhook ? 'delete' : 'disable'
              }, type the webhook name`}{' '}
              <Text as="span" size="sm" css={{ color: '$secondary400', whiteSpace: 'break-spaces' }} weight="bold">
                {webhookName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const webhookNameNormalized = webhookName?.trim() || '';
                if (inputValue === webhookNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box>{' '}
            {`You are ${
              isDeleteWebhook ? 'deleting' : 'disabling'
            } an enabled app. The
            ${
              isDeleteWebhook ? 'deletion' : 'disablement'
            } will affect all the existing users utilizing the app.`}
          </Text>
          <Text size="md">
            {`Are you sure you want to ${
              isDeleteWebhook ? 'delete' : 'disable'
            }  the global webhook?`}
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Yes, I am sure
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default SingleWebhook;
